import React, { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from './ui/button';

const Calendar = ({ events = [], onDateClick, onEventClick }) => {
  const [currentDate, setCurrentDate] = useState(new Date());
  
  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];
  
  const daysInMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  };
  
  const firstDayOfMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth(), 1).getDay();
  };
  
  const prevMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1));
  };
  
  const nextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1));
  };
  
  const formatDate = (year, month, day) => {
    const m = String(month + 1).padStart(2, '0');
    const d = String(day).padStart(2, '0');
    return `${year}-${m}-${d}`;
  };
  
  const getEventsForDate = (dateStr) => {
    return events.filter(event => event.date === dateStr);
  };
  
  const renderCalendar = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const days = daysInMonth(currentDate);
    const firstDay = firstDayOfMonth(currentDate);
    const today = new Date();
    const todayStr = formatDate(today.getFullYear(), today.getMonth(), today.getDate());
    
    const calendar = [];
    
    // Day headers
    const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    calendar.push(
      <div key="headers" className="grid grid-cols-7 gap-2 mb-2">
        {dayNames.map(day => (
          <div key={day} className="text-center text-sm font-semibold text-slate-400 py-2">
            {day}
          </div>
        ))}
      </div>
    );
    
    // Empty cells for days before month starts
    const cells = [];
    for (let i = 0; i < firstDay; i++) {
      cells.push(
        <div key={`empty-${i}`} className="min-h-[100px] p-2"></div>
      );
    }
    
    // Days of the month
    for (let day = 1; day <= days; day++) {
      const dateStr = formatDate(year, month, day);
      const dayEvents = getEventsForDate(dateStr);
      const isToday = dateStr === todayStr;
      
      cells.push(
        <div
          key={day}
          onClick={() => onDateClick && onDateClick(dateStr)}
          className={`min-h-[100px] p-2 border rounded-lg cursor-pointer transition-colors ${
            isToday 
              ? 'bg-blue-500/20 border-blue-500' 
              : 'bg-slate-800/50 border-slate-700 hover:bg-slate-800'
          }`}
        >
          <div className="text-sm font-semibold text-white mb-1">{day}</div>
          <div className="space-y-1">
            {dayEvents.slice(0, 3).map((event, idx) => (
              <div
                key={idx}
                onClick={(e) => {
                  e.stopPropagation();
                  onEventClick && onEventClick(event);
                }}
                className="text-xs px-2 py-1 rounded truncate"
                style={{ backgroundColor: event.color + '40', color: event.color }}
              >
                {event.title}
              </div>
            ))}
            {dayEvents.length > 3 && (
              <div className="text-xs text-slate-400 px-2">
                +{dayEvents.length - 3} more
              </div>
            )}
          </div>
        </div>
      );
    }
    
    calendar.push(
      <div key="days" className="grid grid-cols-7 gap-2">
        {cells}
      </div>
    );
    
    return calendar;
  };
  
  return (
    <div className="bg-slate-900/50 rounded-lg p-4">
      {/* Calendar Header */}
      <div className="flex items-center justify-between mb-4">
        <Button
          size="sm"
          variant="outline"
          onClick={prevMonth}
        >
          <ChevronLeft className="w-4 h-4" />
        </Button>
        <h3 className="text-xl font-bold text-white">
          {monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}
        </h3>
        <Button
          size="sm"
          variant="outline"
          onClick={nextMonth}
        >
          <ChevronRight className="w-4 h-4" />
        </Button>
      </div>
      
      {/* Calendar Grid */}
      {renderCalendar()}
    </div>
  );
};

export default Calendar;
